/**
 * /what-is-a-product-roadmap — SEO landing page for the head term
 * "what is a product roadmap" / "product roadmap" / "product roadmap
 * meaning" / "what is a roadmap" / "project roadmap" / "product
 * development roadmap" (≈19k current organic traffic across countries,
 * KD ~3). The single biggest product-management keyword in the set that
 * we did NOT already have a page for — /product-roadmap-example covers
 * "examples", this covers the definition / top-of-funnel.
 *
 * Dual job: (1) answer "what is a product roadmap" plainly and well —
 * definition up top for the snippet, the real types, roadmap vs release
 * plan vs Gantt (folds the concept-comparison keywords), how to build
 * one; (2) the Draftlize turn — a roadmap is a *view over decisions*, and
 * when a decision under a roadmap item changes the item silently goes
 * stale. Draftlize holds the decisions the roadmap rests on so it can't
 * drift out of sync. Honest: we are NOT a roadmapping tool and say so.
 *
 * English-only + inline (no i18n), mirroring ProductRequirementsDocument.
 * Chinese variant lives in WhatIsAProductRoadmapZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function WhatIsAProductRoadmap() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/what-is-a-product-roadmap"
      documentTitle="What Is a Product Roadmap? Definition + Types · Draftlize"
      documentDescription="A product roadmap is a shared plan of what you'll build and why, over time. What it is, the main types, how to build one — and how to keep it from drifting."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Product roadmaps</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            What is a product roadmap?<br/>
            <em>And the kind that stays true.</em>
          </h1>
          <p className="lede">
            A <em>product roadmap</em> is a shared plan of what you intend to build, in roughly what order, and — the part most roadmaps skip — <em>why</em>. It&apos;s how a team, a leadership group, and increasingly an AI agent stay aligned on direction without a standing meeting. This page does two things: it tells you plainly what a product roadmap is, the types that actually get used, and how to build one — then it&apos;s honest about the part nobody warns you about. A roadmap is a view over a pile of decisions, and the moment one of those decisions changes, the roadmap is quietly wrong and nothing tells you which part.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what is a product roadmap?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            A product roadmap is the agreed answer to <em>&quot;what are we building next, and why&quot;</em> — expressed as themes or outcomes laid out over time. The <em>product roadmap meaning</em>, stripped of ceremony, is direction made legible: a way for everyone downstream to act without re-asking. A good one trades in <em>outcomes</em> (&quot;cut activation time in half&quot;) more than features (&quot;ship onboarding v2&quot;), because outcomes survive contact with reality and feature lists don&apos;t.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            It is not a backlog (that&apos;s the unordered pile of everything), not a release plan (that&apos;s dates and versions for what&apos;s already committed), and not a Gantt chart (that&apos;s task-level scheduling with dependencies). A roadmap sits above all three: it&apos;s strategy made concrete enough to prioritize against, but loose enough that you&apos;re not lying about Q3. More on those distinctions below.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            The format isn&apos;t sacred. A roadmap can be three columns on a whiteboard or a tidy timeline in a dedicated tool. What matters is whether each item ties back to a goal and the decisions behind it — because that&apos;s the thread that breaks first, and when it breaks the roadmap becomes theater.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The formats</span>
            <h2 className="serif">Types of product roadmap <em>(that people actually use).</em></h2>
          </div>
          <p>Five formats cover almost every real roadmap. They&apos;re not exclusive — a team often keeps a Now-Next-Later view for stakeholders and a timeline view for delivery. Pick by how much certainty you honestly have.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Now / Next / Later',
              hint: 'Low certainty',
              body: 'Three buckets instead of dates. Honest about the fact that "later" is fuzzy, which is why it has become the default for product-led teams. Great for stakeholders, terrible for anyone who wants a ship date — by design.',
            },
            {
              k: 'Timeline / Gantt-style',
              hint: 'High certainty',
              body: 'Items placed on actual months or quarters. Useful when commitments are real (a launch, a contract, a dependency on another team). Dangerous when the certainty is fake — a precise date on a guess reads as a promise.',
            },
            {
              k: 'Theme-based',
              hint: 'Direction',
              body: 'Organized around a few strategic themes ("trust", "activation", "enterprise readiness") rather than features. Keeps the conversation at the altitude of why, and lets the specific features under a theme change without the roadmap "changing".',
            },
            {
              k: 'Outcome-based',
              hint: 'Goals',
              body: 'Each item is a measurable result, not a feature — "reduce time-to-first-value to under 5 minutes". The strongest format for keeping teams honest, because you can\'t mark an outcome "done" by shipping; you have to move the number.',
            },
            {
              k: 'Release-based',
              hint: 'Delivery',
              body: 'Grouped by version or release train. Closest to a release plan, and best once work is committed and you\'re coordinating the actual ship. This is where a roadmap hands off to a release plan and a changelog.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          Want copy-paste versions of these? See <a href="/product-roadmap-example" style={{ color: 'var(--amber)' }}>product roadmap examples</a>.
        </p>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Don&apos;t confuse them</span>
            <h2 className="serif">Roadmap vs release plan <em>vs Gantt chart.</em></h2>
          </div>
          <p>Three things that look similar and answer different questions. Mixing them is the most common way a roadmap becomes a list of broken promises.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>Artifact</th>
              <th>Answers</th>
              <th>Time horizon</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Product roadmap</td><td className="yes">What and why, by theme/outcome</td><td>Quarters, loose</td></tr>
            <tr><td>Release plan</td><td className="partial">When committed work ships</td><td>Weeks, firm</td></tr>
            <tr><td>Gantt chart</td><td className="partial">Task order &amp; dependencies</td><td>Days, precise</td></tr>
            <tr><td>Backlog</td><td className="no">Everything, unordered</td><td>None</td></tr>
          </tbody>
        </table>
        <p style={{ maxWidth: 760, marginTop: 18, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>
          The honest rule: the further left you are, the less you should commit to dates. A roadmap promising precise dates is really a release plan in disguise — and the first slip turns the whole thing into fiction. (Once work is committed, a <a href="/release-plan-template" style={{ color: 'var(--amber)' }}>release plan</a> and <a href="/release-notes" style={{ color: 'var(--amber)' }}>release notes</a> take over.)
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">How to build one <em>in five steps.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Start from strategy, not features</h3>
            <p>A roadmap is downstream of <a href="/product-strategy" style={{ color: 'var(--amber)' }}>product strategy</a>. Before any item goes on it, you should be able to name the few bets it serves — who you&apos;re for, the problem, why now. Skip this and you get a feature list with a calendar, which is the most common failure mode.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Gather and frame the inputs</h3>
            <p>Discovery findings, customer feedback, support themes, technical debt, the things sales keeps losing on. Frame each as an opportunity tied to a goal — not a solution yet. This is where <a href="/product-discovery" style={{ color: 'var(--amber)' }}>product discovery</a> feeds the roadmap.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Prioritize honestly</h3>
            <p>Use a framework you&apos;ll actually defend — RICE, WSJF, a weighted scoring model — so the order reflects reach, impact, effort, and confidence, not the loudest voice. See <a href="/prioritization-frameworks" style={{ color: 'var(--amber)' }}>prioritization frameworks</a>.</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">Choose a format and a horizon</h3>
            <p>Pick the format that matches your real certainty (above). Use Now-Next-Later when you&apos;re honest that later is fuzzy; use a timeline only where commitments are firm. Don&apos;t paint precision you don&apos;t have.</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">Keep it current — the hard part</h3>
            <p>A roadmap is most accurate the day you publish it. Decisions move, and the roadmap doesn&apos;t move with them unless someone remembers every downstream item a change touched. That&apos;s the step every guide waves at and nobody does by hand. It&apos;s the rest of this page.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Why roadmaps drift — <em>and the fix.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            First, the honest disclaimer: Draftlize is <strong>not</strong> a roadmapping tool. If you want a beautiful timeline UI, use Productboard, Aha, or ProductPlan — we&apos;ll happily point you there. Draftlize owns one narrow thing none of them do: keeping the <em>decisions underneath</em> the roadmap from quietly going stale.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A roadmap is a view over decisions</h3>
            <p>Every item rests on decisions — a pricing call, a target segment, an assumption about a dependency. The roadmap shows the conclusions, not the reasoning, so when the reasoning changes the picture stays the same while quietly becoming false. The drift is invisible precisely because the roadmap looks finished.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>In Draftlize each decision is an addressable card, and roadmap items, specs, and PRDs link to the decisions they depend on. Change the upstream decision and everything that rested on it auto-flags stale — the way a build system invalidates everything downstream of a changed file. You see exactly what your change broke, the moment you make it, instead of in the next planning review.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your AI agent reads the current version</h3>
            <p>Keep your roadmap tool. Add Draftlize underneath as the decision layer that Claude Code or Cursor reads and writes over MCP — so the agent drafting your next spec already knows the live decisions the roadmap is built on, not the version that was true at last quarter&apos;s planning.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A roadmap doesn&apos;t fail because it was drawn badly. It fails because nothing tells it when one of the decisions underneath it stopped being true.
        </blockquote>
        <cite>Keep your roadmap tool. Add the layer that keeps the decisions under it current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the difference between a product roadmap and a release plan?', a: 'A roadmap says what you intend to build and why, by theme or outcome, over loose timeframes. A release plan says when specific, committed work will ship, in firm dates and versions. Roadmap is upstream and strategic; release plan is downstream and operational.' },
            { q: 'Is a product roadmap the same as a backlog?', a: 'No. A backlog is the unordered pile of everything that might get done. A roadmap is a deliberate, prioritized subset framed around goals. A backlog answers "what could we do"; a roadmap answers "what will we do next, and why".' },
            { q: 'How far into the future should a roadmap go?', a: 'Match the horizon to your real certainty. Most teams keep "now" concrete (this quarter), "next" directional (next quarter or two), and "later" as themes with no dates. Painting precise dates beyond your confidence is the fastest way to turn a roadmap into broken promises.' },
            { q: 'Who owns the product roadmap?', a: 'The product manager owns it, but it is built from inputs across engineering, design, sales, support, and leadership, and it serves all of them. Ownership means keeping it honest and current — not deciding everything alone.' },
            { q: 'What tool should I use for a product roadmap?', a: 'For the roadmap view itself, dedicated tools like Productboard, Aha, or ProductPlan are strong. Draftlize is not a roadmapping tool — it sits underneath whatever you use, holding the decisions each roadmap item depends on so they do not silently drift out of date.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep the decisions under your roadmap <em>current — free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep the roadmap tool you like. Add Draftlize for the one thing none of them do: making sure every decision your roadmap rests on stays current — and your AI agent always reads the live version.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
